import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { CheckCircle, XCircle, AlertTriangle, Shield, ChevronDown, ChevronUp } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/resources" className="dark-nav-link">RESOURCES</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-accordion-item ${isOpen ? 'active' : ''}`}>
      <button className="faq-accordion-button" onClick={() => setIsOpen(!isOpen)}>
        <span className="faq-accordion-question">{question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div className="faq-accordion-content">
          <p className="faq-answer">{answer}</p>
        </div>
      )}
    </div>
  );
};

const SeedPhraseMissing1Word = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Seed Phrase Missing 1 Word: Safe Recovery Guide (UK)</title>
        <meta name="description" content="Missing one seed word? Learn safe steps, what to avoid, and when recovery is feasible. Non-custodial, offline process. Free confidential assessment." />
        <meta name="keywords" content="seed phrase missing one word, BIP39 recovery, seed phrase recovery UK, missing seed word, crypto wallet recovery" />
        <link rel="canonical" href="https://arcana-crypto.com/seed-recovery-seo.html" />
      </Helmet>

      <Navigation />
      
      <article className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container" style={{maxWidth: '900px'}}>
          
          {/* Header */}
          <header>
            <h1 className="section-title" style={{fontSize: '42px', textAlign: 'left', marginBottom: '16px'}}>
              Seed Phrase Missing 1 Word: What to Do (and What to Avoid)
            </h1>
            <p className="section-intro" style={{fontSize: '18px', marginBottom: '40px'}}>
              If you are missing a single word from a BIP39 seed phrase, recovery can be feasible. 
              The key is to proceed safely, avoid data exposure, and reduce guesswork with a structured approach.
            </p>
          </header>

          {/* Key Takeaways */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Key Takeaways
            </h2>
            <ul className="column-list">
              <li>Do not share your full seed phrase with anyone.</li>
              <li>One missing word is often technically feasible, but case-dependent.</li>
              <li>Language, wallet type, and derivation path can materially change difficulty.</li>
              <li>Avoid online "seed recovery" tools and random attempts.</li>
              <li>A professional assessment can estimate feasibility without taking custody of funds.</li>
            </ul>
          </section>

          {/* What to Do */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <CheckCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Do
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Stop and document what you know (words, order, wallet/app).</li>
                <li>Confirm the BIP39 language.</li>
                <li>Identify wallet type and network (BTC vs ETH etc.).</li>
                <li>Collect non-sensitive hints (first receiving address, xpub if available, screenshots, approximate creation date).</li>
                <li>Work offline. Never paste seeds into websites.</li>
              </ul>
            </div>
          </section>

          {/* What to Avoid */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <XCircle size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              What to Avoid
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Do not use web-based recovery tools.</li>
                <li>Do not DM your seed to "support".</li>
                <li>Avoid repeated random attempts in a live wallet.</li>
                <li>Do not assume the missing word is a synonym (BIP39 is a fixed list).</li>
              </ul>
            </div>
          </section>

          {/* Technical Explanation */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              Technical Explanation (Simplified)
            </h2>
            <div className="legal-content">
              <p>
                A <strong>BIP39 seed phrase</strong> encodes entropy plus a checksum. With <strong>one missing word</strong>, 
                candidates come from a 2048-word list, and checksum validation can eliminate many invalid combinations.
              </p>
              <p style={{marginTop: '16px'}}>
                Feasibility depends on word position, correct language, wallet derivation path, and 
                whether you have an address/xpub hint to validate candidates. Our <strong>non-custodial</strong> approach 
                means you retain full control of your funds throughout the recovery process.
              </p>
              <p style={{marginTop: '16px'}}>
                For cases with a <strong>seed phrase missing 1 word</strong>, computational requirements are typically 
                manageable (hours to days), but <strong>BIP39</strong> language accuracy and wallet type identification 
                materially affect success probability.
              </p>
            </div>
          </section>

          {/* Non-Custodial & Privacy */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              <Shield size={32} style={{display: 'inline', marginRight: '12px', color: 'var(--oro-arcana)', verticalAlign: 'middle'}} />
              Non-Custodial & Privacy
            </h2>
            <div className="about-card">
              <ul className="column-list">
                <li>Strictly non-custodial: we do not take custody of funds.</li>
                <li>We do not ask for your full seed via form, email, WhatsApp, or Telegram.</li>
                <li>Work is performed offline; communications are encrypted.</li>
                <li>Data is minimised and deleted after completion in line with GDPR principles.</li>
              </ul>
            </div>
          </section>

          {/* When Recovery is Feasible */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              When Recovery is Feasible
            </h2>
            <div className="two-column">
              <div>
                <h3 className="column-title" style={{fontSize: '20px', color: 'var(--oro-arcana)'}}>
                  MORE LIKELY ✓
                </h3>
                <p className="legal-content">
                  When you are sure the other words are correct, you know the language, and you can 
                  provide wallet/app details plus at least one address or xpub.
                </p>
              </div>
              <div>
                <h3 className="column-title" style={{fontSize: '20px', color: 'var(--oro-arcana)'}}>
                  LESS LIKELY ✗
                </h3>
                <p className="legal-content">
                  When multiple words are uncertain, language is unknown, or the wallet used a 
                  non-standard derivation path with no hints (or more expensive).
                </p>
              </div>
            </div>
          </section>

          {/* What We Need */}
          <section style={{marginBottom: '60px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '24px'}}>
              What We Need for Assessment
            </h2>
            <div className="pricing-notes">
              <ul className="column-list">
                <li>Network (BTC/ETH/LTC/DOGE)</li>
                <li>Wallet/app name</li>
                <li>Seed length (12/18/24)</li>
                <li>Missing word position (if known)</li>
                <li>BIP39 language (best guess)</li>
                <li>Any public address/xpub (optional)</li>
                <li>Time sensitivity</li>
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section style={{marginBottom: '80px'}}>
            <h2 className="about-subtitle" style={{textAlign: 'left', fontSize: '28px', marginBottom: '32px'}}>
              Frequently Asked Questions
            </h2>
            <div className="faq-accordion">
              <FAQItem 
                question="Do you guarantee recovery?"
                answer="No. Case-specific feasibility only."
              />
              <FAQItem 
                question="Will you ask for my full seed phrase?"
                answer="Not via form/chat. Sensitive handling is discussed securely if required."
              />
              <FAQItem 
                question="How long does it take?"
                answer="Hours to days, depending on complexity."
              />
              <FAQItem 
                question="Can I try myself first?"
                answer="Yes, but avoid online tools and exposure."
              />
              <FAQItem 
                question="Does the missing word have to be exact?"
                answer="Yes."
              />
              <FAQItem 
                question="What affects cost?"
                answer="Uncertainty, wallet type, derivation complexity, compute time."
              />
              <FAQItem 
                question="What if recovery fails?"
                answer="We stop within agreed scope and provide a technical summary."
              />
            </div>
          </section>

          {/* CTA */}
          <section className="cta-section" style={{marginTop: '80px', padding: '60px 48px'}}>
            <h2 className="cta-title" style={{fontSize: '36px'}}>
              Missing 1 Seed Word?
            </h2>
            <p className="cta-subtitle" style={{marginBottom: '40px'}}>
              Start with a free, confidential case assessment
            </p>
            <button className="btn-primary" style={{fontSize: '18px', padding: '18px 40px'}} onClick={() => window.location.href = '/#assessment'}>
              START FREE ASSESSMENT
            </button>
            <p className="hero-small-text" style={{marginTop: '24px'}}>
              Non-custodial • Confidential • Professional
            </p>
            <div style={{marginTop: '32px'}}>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '12px'}}>
                <strong>Related Services:</strong>
              </p>
              <a href="/seed-phrase-recovery" className="card-link" style={{fontSize: '16px', marginRight: '20px'}}>
                Seed Phrase Recovery Service →
              </a>
              <a href="/crypto-recovery-service-uk" className="card-link" style={{fontSize: '16px'}}>
                UK Recovery Services →
              </a>
            </div>
          </section>

          {/* Breadcrumb Back */}
          <div style={{marginTop: '60px', textAlign: 'center'}}>
            <a href="/resources" className="card-link" style={{fontSize: '16px'}}>
              ← Back to All Resources
            </a>
          </div>

        </div>
      </article>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SeedPhraseMissing1Word;
